import { Box, MenuItem, Popper, Select, Paper, Typography, useTheme, useMediaQuery, FormControlLabel, Checkbox } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import dayjs from 'dayjs';
import { setDbDateRange } from '../../../redux/reducers/DashboardSlice';
import { half, months, periodDropdown, asOnPeriodDropdown, quarter, shortHalf, shortMonth, shortQuarter } from './data';
import { useThemes } from '../../../components/Theme/theme';
import BasedOnSelect from './BasedOnSelect';
import { DateRangeRsuite } from '../../../components';
import { useFetchOpeningBalanceDateQuery, useLazyFetchOpeningBalanceDateQuery } from '../../../redux/apis/openingbalacedate.api';
const ActionItems = ({
  page,
  title,
  // selectedTab,
  outstandingPeriod,
  openingBalanceRequired = true,
  isCustomDateModalOpen,
  setIsCustomDateModalOpen,
  anchorEl,
  setAnchorEl,
  showDateRangeText = true,
  includeOpeningBalance,
  setIncludeOpeningBalance,
}) => {
  const dispatch = useDispatch();
  const { theme } = useThemes();

  const useNewTheme = useTheme();
  const matches = useMediaQuery(useNewTheme.breakpoints.down('sm'));
  const dashboardData = useSelector((state) => state?.DashboardDataReducer);
  const [preventDate, setPreventDate] = useState('');
  const [openfn] = useLazyFetchOpeningBalanceDateQuery();
  useEffect(() => {
    const handleBeforeUnload = () => {
      setReloadFlag();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    if (getReloadFlag()) {
    }

    const ggfn = async () => {
      const openDate = await openfn(
        {
          invoicetypecd: 'Sales',
          invoiceid: 0,
        },
        true
      );
      setPreventDate(openDate.data.getMinMaxInvoiceDate.openingbalancedate);
    };
    ggfn();

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Helper function to check if a month should be disabled
  const isMonthDisabled = (monthId) => {
    if (!preventDate) return false;
    const preventDateObj = new Date(preventDate);
    const preventMonth = preventDateObj.getMonth() + 1; // getMonth() returns 0-11, we need 1-12

    // Financial year structure: Apr(4), May(5), Jun(6), Jul(7), Aug(8), Sep(9), Oct(10), Nov(11), Dec(12), Jan(1), Feb(2), Mar(3)
    // We need to disable months that come BEFORE the preventDate in the financial year sequence

    // If preventDate is in Oct-Dec (10-12), disable Apr-Sep (4-9)
    if (preventMonth >= 10 && preventMonth <= 12) {
      return monthId >= 4 && monthId <= 9;
    }
    // If preventDate is in Jan-Mar (1-3), disable Apr-Dec (4-12) of previous year
    else if (preventMonth >= 1 && preventMonth <= 3) {
      return monthId >= 4 && monthId <= 12;
    }
    // If preventDate is in Apr-Sep (4-9), disable months before preventMonth
    else if (preventMonth >= 4 && preventMonth <= 9) {
      return monthId >= 4 && monthId < preventMonth;
    }

    return false;
  };

  // Helper function to check if a quarter should be disabled
  const isQuarterDisabled = (quarterId) => {
    if (!preventDate) return false;
    const preventDateObj = new Date(preventDate);
    const preventMonth = preventDateObj.getMonth() + 1;

    // Financial year quarters: Q1: Apr-Jun (4-6), Q2: Jul-Sep (7-9), Q3: Oct-Dec (10-12), Q4: Jan-Mar (1-3)
    // We need to disable quarters that come BEFORE the preventDate in the financial year sequence

    // If preventDate is in Oct-Dec (10-12), disable Q1 and Q2
    if (preventMonth >= 10 && preventMonth <= 12) {
      return quarterId === 'Q1' || quarterId === 'Q2';
    }
    // If preventDate is in Jan-Mar (1-3), disable Q1, Q2, Q3 (previous year's quarters)
    else if (preventMonth >= 1 && preventMonth <= 3) {
      return quarterId === 'Q1' || quarterId === 'Q2' || quarterId === 'Q3';
    }
    // If preventDate is in Jul-Sep (7-9), disable Q1
    else if (preventMonth >= 7 && preventMonth <= 9) {
      return quarterId === 'Q1';
    }
    // If preventDate is in Apr-Jun (4-6), no quarters disabled
    else if (preventMonth >= 4 && preventMonth <= 6) {
      return false;
    }

    return false;
  };

  // Helper function to check if a half-year should be disabled
  const isHalfDisabled = (halfId) => {
    if (!preventDate) return false;
    const preventDateObj = new Date(preventDate);
    const preventMonth = preventDateObj.getMonth() + 1;

    // Financial year halves: H1: Apr-Sep (4-9), H2: Oct-Mar (10-12, 1-3)
    // We need to disable halves that come BEFORE the preventDate in the financial year sequence

    // If preventDate is in Oct-Dec (10-12), disable H1 (Apr-Sep)
    if (preventMonth >= 10 && preventMonth <= 12) {
      return halfId === 'H1';
    }
    // If preventDate is in Jan-Mar (1-3), disable H1 (Apr-Sep of previous year)
    else if (preventMonth >= 1 && preventMonth <= 3) {
      return halfId === 'H1';
    }
    // If preventDate is in Apr-Sep (4-9), no halves disabled
    else if (preventMonth >= 4 && preventMonth <= 9) {
      return false;
    }

    return false;
  };

  // Helper function to check if "Today" option should be disabled
  const isTodayDisabled = () => {
    if (!preventDate) return false;
    const today = new Date();
    const preventDateObj = new Date(preventDate);

    // Set both dates to start of day for accurate comparison
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const preventDateStart = new Date(preventDateObj.getFullYear(), preventDateObj.getMonth(), preventDateObj.getDate());

    return todayStart < preventDateStart;
  };

  // Helper function to get the appropriate start timestamp for a period
  const getPeriodStartTimestamp = (periodType, subPeriodValue, normalStartTimestamp) => {
    if (!preventDate) return normalStartTimestamp;

    const preventDateObj = new Date(preventDate);
    const preventDateTime = preventDateObj.getTime() / 1000; // Convert to seconds

    // Check if the period includes the preventDate
    let periodIncludesPreventDate = false;

    if (periodType === 'month' && subPeriodValue) {
      const monthVal = getFinancialMonthStartAndEnd(subPeriodValue, userPreferencesState?.financialYear?.startyear);
      // Check if preventDate falls within this month
      periodIncludesPreventDate = preventDateTime >= monthVal.startOfMonthUnix && preventDateTime <= monthVal.endOfMonthUnix;
    } else if (periodType === 'quarter' && subPeriodValue) {
      // Check if preventDate falls within the selected quarter
      const quarterIndex = ['Q1', 'Q2', 'Q3', 'Q4'].indexOf(subPeriodValue);
      if (quarterIndex !== -1) {
        const quarterData = quarters.map((q) => {
          const startDate = new Date(
            q.startMonth >= 3 ? userPreferencesState?.financialYear?.startyear : userPreferencesState?.financialYear?.endyear,
            q.startMonth,
            1,
            0,
            0,
            0
          );
          const endDate = new Date(
            q.endMonth >= 3 ? userPreferencesState?.financialYear?.startyear : userPreferencesState?.financialYear?.endyear,
            q.endMonth + 1,
            0,
            23,
            59,
            59
          );
          return {
            startTimestamp: Math.floor(startDate.getTime() / 1000),
            endTimestamp: Math.floor(endDate.getTime() / 1000),
          };
        });

        if (quarterData[quarterIndex]) {
          periodIncludesPreventDate =
            preventDateTime >= quarterData[quarterIndex].startTimestamp && preventDateTime <= quarterData[quarterIndex].endTimestamp;
        }
      }
    } else if (periodType === 'half' && subPeriodValue) {
      // Check if preventDate falls within the selected half-year
      const halfIndex = ['H1', 'H2'].indexOf(subPeriodValue);
      if (halfIndex !== -1) {
        const halfData = quarters.slice(4).map((h) => {
          const startDate = new Date(
            h.startMonth >= 3 ? userPreferencesState?.financialYear?.startyear : userPreferencesState?.financialYear?.endyear,
            h.startMonth,
            1,
            0,
            0,
            0
          );
          const endDate = new Date(
            h.endMonth >= 3 ? userPreferencesState?.financialYear?.startyear : userPreferencesState?.financialYear?.endyear,
            h.endMonth + 1,
            0,
            23,
            59,
            59
          );
          return {
            startTimestamp: Math.floor(startDate.getTime() / 1000),
            endTimestamp: Math.floor(endDate.getTime() / 1000),
          };
        });

        if (halfData[halfIndex]) {
          periodIncludesPreventDate =
            preventDateTime >= halfData[halfIndex].startTimestamp && preventDateTime <= halfData[halfIndex].endTimestamp;
        }
      }
    }

    // If period includes preventDate, use preventDate as start timestamp, otherwise use normal start
    return periodIncludesPreventDate ? preventDateTime : normalStartTimestamp;
  };

  const userPreferencesState = useSelector((state) => state.userPrefrenceandReducer);
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  let thisYear;
  if (currentMonth >= 4) {
    thisYear = currentYear;
  } else {
    thisYear = currentYear - 1;
  }

  const hasFinancialYear = userPreferencesState?.financialYear && Object.keys(userPreferencesState?.financialYear).length > 0;
  const FYID = userPreferencesState?.financialYear?.financialyearid;
  const isCurrentFinancialYear = thisYear == userPreferencesState?.financialYear?.startyear;

  const currentPageDateRange = dashboardData[page];

  const RELOAD_FLAG_KEY = 'justReloaded';

  function setReloadFlag() {
    localStorage.setItem(RELOAD_FLAG_KEY, '1');
  }

  function getReloadFlag() {
    return localStorage.getItem(RELOAD_FLAG_KEY) === '1';
  }

  function clearReloadFlag() {
    localStorage.removeItem(RELOAD_FLAG_KEY);
  }

  const periodDef = outstandingPeriod ? outstandingPeriod : '';
  const [subPeriod, selectSubPeriod] = useState([]);
  const [period, setPeriod] = useState(periodDef);
  const [periodCheck, setPeriodCheck] = useState(period);
  const [openMenuId, setOpenMenuId] = useState(false);

  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);
  const wrapperRef = useRef(null);
  const startTimeStamp = dashboardData[page]?.startTimeStamp;
  // const startTimeStamp = dayjs(preventDate).unix();

  const endTimeStamp = dashboardData[page]?.endTimeStamp;

  const subDef = Object.keys(dashboardData.report).length === 1 && page === 'report' ? currentMonth : '';
  const [subPeriodValue, selectSubPeriodValue] = useState(subDef);

  const handleOpenSubMenu = (event, text) => {
    setPeriod(text);
    if (text === 'day' || text === 'year') {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
      setOpenMenuId(true);
    }
  };

  const quarters = [
    { name: 'Q1', startMonth: 3, endMonth: 5 }, // April - June
    { name: 'Q2', startMonth: 6, endMonth: 8 }, // July - September
    { name: 'Q3', startMonth: 9, endMonth: 11 }, // October - December
    { name: 'Q4', startMonth: 0, endMonth: 2 }, // January - March (next year)
    { name: 'H1', startMonth: 3, endMonth: 8 }, // April - September
    { name: 'H2', startMonth: 9, endMonth: 2 },
  ];

  useEffect(() => {
    function handleClickOutside() {
      if (wrapperRef.current) {
        // setAnchorEl(null); // close Popper
      }
      setOpenMenuId(false); // optionally close Select
    }
    handleClickOutside();
    return () => {
      wrapperRef.current = null;
    };
  }, []);
  useEffect(() => {
    if (period === 'year') {
      setPeriod('year');

      updateDateRange(dayjs(preventDate).unix(), currentFinancialYear?.enddate, period);
    }
  }, [preventDate]);
  const prevTabRef = useRef();
  const reloadVar = JSON.parse(localStorage.getItem('justReloaded')) || null;
  const updateDateRange = (startTimeStamp, endTimeStamp, period) => {
    dispatch(
      setDbDateRange({
        outerKey: outstandingPeriod && page === 'report' ? 'bsPeriod' : page,
        innerKey: 'startTimeStamp',
        value: startTimeStamp,
      })
    );
    dispatch(
      setDbDateRange({
        outerKey: outstandingPeriod && page === 'report' ? 'bsPeriod' : page,
        innerKey: 'endTimeStamp',
        value: endTimeStamp,
      })
    );
    dispatch(
      setDbDateRange({
        outerKey: outstandingPeriod && page === 'report' ? 'bsPeriod' : page,
        innerKey: page === 'report' && !outstandingPeriod ? 'period' : 'range',
        value: period,
      })
    );

    dispatch(
      setDbDateRange({
        outerKey: outstandingPeriod && page === 'report' ? 'bsPeriod' : page,
        innerKey: 'fyId',
        value: FYID,
      })
    );
    if (period === 'date') {
      setAnchorEl(null);
      setFrom(null);
      setTo(null);
    }
  };
  // useEffect(() => {
  //   setPeriod('year');
  //   if (isCurrentFinancialYear && page === 'report') {
  //     if (selectedTab === 9) {
  //       setPeriod('year');
  //     } else if (selectedTab === 5 && reloadVar === null) {
  //       const currentQuarter = Math.floor(new Date().getMonth() / 4);
  //       setPeriod('quarter');
  //       selectSubPeriodValue('Q' + currentQuarter);
  //     } else if (selectedTab === 7) {
  //       setPeriod(dashboardData['bsPeriod'].range);
  //     } else if ((prevTabRef.current === 5 || !prevTabRef.current) && reloadVar === null) {
  //       setPeriod('month');
  //       const monthVal = getFinancialMonthStartAndEnd(subPeriodValue, userPreferencesState?.financialYear?.startyear);
  //       const adjustedStartTimestamp = getPeriodStartTimestamp('month', subPeriodValue, monthVal.startOfMonthUnix);
  //       updateDateRange(adjustedStartTimestamp, monthVal.endOfMonthUnix, period);
  //       const currentMonth = new Date().getMonth() + 1; // months are 0-based
  //       selectSubPeriodValue(currentMonth);
  //     } else {
  //       setPeriod(currentPageDateRange?.period);
  //     }
  //     prevTabRef.current = selectedTab;
  //     if (reloadVar) {
  //       clearReloadFlag();
  //     }
  //   } else if (
  //     (currentPageDateRange?.startTimeStamp === null || (dashboardData['bsPeriod']?.startTimeStamp === null && selectedTab === 7)) &&
  //     isCurrentFinancialYear === false
  //   ) {
  //     setPeriod('year');
  //     setFinancialYearDateRange();
  //   } else if (selectedTab === 7) {
  //     setPeriod(dashboardData['bsPeriod']?.range);
  //   } else {
  //     if (!currentPageDateRange?.startTimeStamp && isCurrentFinancialYear === false) {
  //       setPeriod('year');

  //       setFinancialYearDateRange();
  //     } else {
  //       setPeriod(currentPageDateRange?.period);
  //     }
  //   }
  // }, [selectedTab]);

  // Function to format date as "1st April 2025"
  const formatDate = (date) => {
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'long' });
    const year = date.getFullYear();

    // Add ordinal suffix to the day
    const ordinal = day === 1 || day === 21 || day === 31 ? 'st' : day === 2 || day === 22 ? 'nd' : day === 3 || day === 23 ? 'rd' : 'th';

    return `${day}${ordinal} ${month} ${year}`;
  };

  // Generate quarter data
  const quarterData = quarters.map((q) => {
    const startDate = new Date(
      q.startMonth >= 3 ? userPreferencesState?.financialYear?.startyear : userPreferencesState?.financialYear?.endyear, // If Q4, use next year
      q.startMonth,
      1,
      0,
      0,
      0
    );

    const endDate = new Date(
      q.endMonth >= 3 ? userPreferencesState?.financialYear?.startyear : userPreferencesState?.financialYear?.endyear, // If Q4, use next year
      q.endMonth + 1,
      0, // Day 0 gives the last day of the month
      23,
      59,
      59
    );

    return {
      quarter: q.name,
      formattedRange: `${formatDate(startDate)} to ${formatDate(endDate)}`,
      startTimestamp: Math.floor(startDate.getTime() / 1000), // Unix timestamp
      endTimestamp: Math.floor(endDate.getTime() / 1000), // Unix timestamp
    };
  });

  const getTodayStartAndEndTimestamps = () => {
    const startOfDay = dayjs().startOf('day').unix();
    const endOfDay = dayjs().endOf('day').unix();
    updateDateRange(startOfDay, endOfDay, period);
  };
  const currentFinancialYear = userPreferencesState?.financialYear;
  const setFinancialYearDateRange = () => {
    if (currentFinancialYear && Object.keys(currentFinancialYear).length > 0) {
      updateDateRange(dayjs(preventDate).unix(), currentFinancialYear?.enddate, period);
    }
  };

  const getFinancialMonthStartAndEnd = (month, year) => {
    // Adjust the year based on financial year (April–March)
    const isBeforeApril = month < 4; // Jan, Feb, Mar belong to the previous financial year
    const financialYear = isBeforeApril ? Number(year) + 1 : Number(year);

    const startOfMonth = dayjs(`${financialYear}-${String(month).padStart(2, '0')}-01`).startOf('month');
    const endOfMonth = startOfMonth.endOf('month');

    return {
      month: month,
      financialYear: `${financialYear}-${financialYear + 1}`, // Example: "2024-2025"
      startOfMonthUnix: startOfMonth.unix(), // Start timestamp in seconds
      endOfMonthUnix: endOfMonth.unix(), // End timestamp in seconds
      startFormatted: startOfMonth.format('YYYY-MM-DD'),
      endFormatted: endOfMonth.format('YYYY-MM-DD'),
    };
  };
  useEffect(() => {
    let newPeriod;

    switch (page) {
      case 'itemPeriod':
      case 'pandlPeriod':
        newPeriod = currentPageDateRange.startTimeStamp && currentPageDateRange.range ? currentPageDateRange.range : 'year';
        setPeriod(newPeriod);
        break;

      case 'bsPeriod':
        newPeriod =
          currentPageDateRange.startTimeStamp && currentPageDateRange.range
            ? currentPageDateRange.range
            : isCurrentFinancialYear
            ? 'day'
            : 'year';
        setPeriod(newPeriod);
        break;

      case 'report':
        newPeriod = outstandingPeriod
          ? outstandingPeriod
          : currentPageDateRange.startTimeStamp
          ? currentPageDateRange.period
          : isCurrentFinancialYear
          ? 'month'
          : 'year';
        setPeriod(newPeriod);

        break;

      default:
        setPeriod(newPeriod);
    }

    if (newPeriod === 'day') {
      getTodayStartAndEndTimestamps();
    } else if (newPeriod === 'year') {
      setFinancialYearDateRange();
    }
  }, [page]);
  useEffect(() => {
    switch (period) {
      case 'day':
        selectSubPeriod([]);
        getTodayStartAndEndTimestamps();
        setOpenMenuId(false);
        break;
      case 'month':
        selectSubPeriodValue('');
        selectSubPeriod(matches ? shortMonth : months);
        break;

      case 'quarter':
        // if (selectedTab == 5) {
        const currentQuarter = Math.floor(new Date().getMonth() / 4);

        selectSubPeriodValue('Q' + currentQuarter);
        // } else {
        //   selectSubPeriodValue('');
        // }
        selectSubPeriod(matches ? shortQuarter : quarter);
        setPeriod('quarter');
        break;

      case 'half':
        selectSubPeriodValue('');
        selectSubPeriod(matches ? shortHalf : half);
        setPeriod('half');
        break;

      case 'year':
        selectSubPeriod([]);
        setPeriod('year');
        setFinancialYearDateRange();
        setOpenMenuId(false);
        break;
      // case 'date':
      //   setFrom(currentFinancialYear?.startdate);
      //   setTo(currentFinancialYear?.enddate);
    }
  }, [period]);

  useEffect(() => {
    if (period === 'month') {
      if (subPeriodValue) {
        const monthVal = getFinancialMonthStartAndEnd(subPeriodValue, userPreferencesState?.financialYear?.startyear);
        const adjustedStartTimestamp = getPeriodStartTimestamp('month', subPeriodValue, monthVal.startOfMonthUnix);
        updateDateRange(adjustedStartTimestamp, monthVal.endOfMonthUnix, period);
      }
    } else {
      switch (subPeriodValue) {
        case 'Q1':
          const q1StartTimestamp = getPeriodStartTimestamp('quarter', 'Q1', quarterData[0].startTimestamp);
          updateDateRange(q1StartTimestamp, quarterData[0].endTimestamp, period);
          setOpenMenuId(false);
          break;
        case 'Q2':
          const q2StartTimestamp = getPeriodStartTimestamp('quarter', 'Q2', quarterData[1].startTimestamp);
          updateDateRange(q2StartTimestamp, quarterData[1].endTimestamp, period);
          setOpenMenuId(false);
          break;
        case 'Q3':
          const q3StartTimestamp = getPeriodStartTimestamp('quarter', 'Q3', quarterData[2].startTimestamp);
          updateDateRange(q3StartTimestamp, quarterData[2].endTimestamp, period);
          setOpenMenuId(false);
          break;
        case 'Q4':
          const q4StartTimestamp = getPeriodStartTimestamp('quarter', 'Q4', quarterData[3].startTimestamp);
          updateDateRange(q4StartTimestamp, quarterData[3].endTimestamp, period);
          setOpenMenuId(false);
          break;
        case 'H1':
          const h1StartTimestamp = getPeriodStartTimestamp('half', 'H1', quarterData[4].startTimestamp);
          updateDateRange(h1StartTimestamp, quarterData[4].endTimestamp, period);
          setOpenMenuId(false);
          break;
        case 'H2':
          const h2StartTimestamp = getPeriodStartTimestamp('half', 'H2', quarterData[5].startTimestamp);
          updateDateRange(h2StartTimestamp, quarterData[5].endTimestamp, period);
          setOpenMenuId(false);
          break;
        case (4, 5, 6, 7, 8, 9, 10, 11, 12):
          setOpenMenuId(false);
          break;
      }
    }
  }, [subPeriodValue]);

  useEffect(() => {
    if (anchorEl === null) {
      setOpenMenuId(false);
    } else {
      setOpenMenuId(true);
    }
  }, [anchorEl]);
  const handleSubperiod = (val) => {
    selectSubPeriodValue(val);
    setAnchorEl(null);
  };

  const isDifferentYear = thisYear != userPreferencesState?.financialYear?.startyear;
  const isAsOnDateRange = page === 'bsPeriod' || outstandingPeriod;
  const baseDropdown = isAsOnDateRange ? asOnPeriodDropdown : periodDropdown;

  const data = isDifferentYear ? baseDropdown.filter((menu) => menu.id !== 'day') : baseDropdown;
  let dateText = null;
  const formattedStart = moment(dashboardData['bsPeriod']?.startTimeStamp * 1000).format('Do MMMM YYYY');
  const formattedEnd = moment(dashboardData['bsPeriod']?.endTimeStamp * 1000).format('Do MMMM YYYY');
  // const formattedEndOut = moment(dashboardData['bsPeriod']?.endTimeStamp * 1000).format('Do MMMM YYYY');
  if ((period === 'day' || outstandingPeriod) && period !== 'date') {
    dateText = `${formattedEnd}`;
  } else {
    dateText = `${formattedStart} to ${formattedEnd}`;
  }

  if (!hasFinancialYear || !data?.length || !period) {
    return null;
  }

  function formatPeriodText(startTs, endTs) {
    if (!startTs || !endTs) return '';

    const fmt = matches ? 'Do MMM YYYY' : 'Do MMMM YYYY';
    const today = dayjs().startOf('day');
    const start = dayjs.unix(startTs).startOf('day');
    const end = dayjs.unix(endTs).startOf('day');

    if (start.isSame(today) && end.isSame(today)) {
      return today.format(fmt);
    }

    if (title === 'As on') {
      return end.format(fmt);
    }

    if (start.isSame(end)) {
      return start.format(fmt);
    }

    return `${start.format(fmt)} – ${end.format(fmt)}`;
  }

  return (
    <Box>
      {/*On small devices, show the title above the selection dropdown.*/}
      <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'flex-start', mb: '-15px' }}>
        <Typography
          variant="body2"
          sx={{
            p: 0,
            fontSize: { xs: '12px', sm: '14px' },
            pl: '5px',
          }}
        >
          <span> {title} </span>
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, alignItems: 'center', gap: '10px' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            py: 1,
          }}
          ref={wrapperRef}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'start',
            }}
          >
            {' '}
            {outstandingPeriod && (
              <>
                <BasedOnSelect />
                <Typography
                  sx={{
                    marginTop: '5px',
                    marginRight: '6px',
                    color: theme?.palette?.TmId.id.toString().includes('9') ? 'white' : 'black',
                    whiteSpace: 'nowrap',
                  }}
                  variant="subtitle1"
                >
                  {title}
                </Typography>
              </>
            )}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
              <Select
                size="medium"
                value={period}
                sx={{
                  minWidth: '185px',
                }}
                MenuProps={{
                  PaperProps: {
                    elevation: 0,
                    sx: {
                      boxShadow: 'none',
                      border: '1px solid #ccc',
                    },
                  },
                }}
                open={openMenuId}
                onClose={() => {
                  setOpenMenuId(false);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  let countff = 0;
                  setPeriodCheck((prev) => {
                    if (prev !== period) {
                      return period;
                    } else {
                      countff++;
                      return period;
                    }
                  });
                  if (countff > 0 && period !== 'date') {
                    setAnchorEl(null);
                  }
                  if (openMenuId === false) {
                    setOpenMenuId(true);
                    setAnchorEl(null);
                  }
                }}
              >
                {data.map((dat) => {
                  const isDisabled = dat.id === 'day' ? isTodayDisabled() : false;

                  return (
                    <MenuItem
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!isDisabled) {
                          handleOpenSubMenu(event, dat.id);
                        }
                      }}
                      sx={{
                        boxShadow: 'none',
                        padding: '0px 10px',
                        marginTop: '2px',
                        opacity: isDisabled ? 0.5 : 1,
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        color: isDisabled ? '#999' : 'inherit',
                        '&:hover': {
                          fontWeight: isDisabled ? 'normal' : 'bold',
                          fontSize: isDisabled ? '14px' : '14px',
                        },
                      }}
                      value={dat.id}
                      disabled={isDisabled}
                    >
                      {dat.label}
                    </MenuItem>
                  );
                })}
                {subPeriod && subPeriod?.length > 0 && period !== 'date' && (
                  <Popper
                    open={Boolean(anchorEl)}
                    anchorReference="anchorPosition"
                    anchorPosition={anchorEl}
                    anchorEl={anchorEl}
                    placement="right-start"
                    style={{
                      zIndex: 1300,
                    }}
                  >
                    <Paper elevation={3} style={{ padding: '10px' }}>
                      {subPeriod.map((menu) => {
                        const isDisabled =
                          period === 'month'
                            ? isMonthDisabled(menu.id)
                            : period === 'quarter'
                            ? isQuarterDisabled(menu.id)
                            : period === 'half'
                            ? isHalfDisabled(menu.id)
                            : false;

                        return (
                          <MenuItem
                            value={menu.id}
                            onClick={() => !isDisabled && handleSubperiod(menu.id)}
                            style={{
                              padding: '10px',
                              opacity: isDisabled ? 0.5 : 1,
                              cursor: isDisabled ? 'not-allowed' : 'pointer',
                              color: isDisabled ? '#999' : 'inherit',
                            }}
                            disabled={isDisabled}
                          >
                            {menu.label}
                          </MenuItem>
                        );
                      })}
                    </Paper>
                  </Popper>
                )}

                {period === 'date' && anchorEl?.getBoundingClientRect()?.top > 100 && (
                  <Popper
                    open={Boolean(anchorEl)}
                    anchorReference="anchorPosition"
                    anchorPosition={anchorEl}
                    anchorEl={anchorEl}
                    placement={matches ? 'bottom' : 'right-start'}
                    style={{
                      zIndex: 1300,
                      padding: '0px',
                    }}
                  >
                    <Paper elevation={3} style={{ overflow: 'scroll', padding: '0px' }}>
                      <DateRangeRsuite
                        value={[from, to]}
                        className="hide-input-outline hide-input-border"
                        restrictToFinancialYear={true}
                        minDate={preventDate ? dayjs(preventDate).subtract(1, 'day').toDate() : null}
                        onChange={(e) => {
                          // Check if selected dates are before preventDate
                          // const preventDateObj = preventDate ? new Date(preventDate) : null;
                          // const fromDate = e.from ? new Date(e.from) : null;
                          // const toDate = e.to ? new Date(e.to) : null;
                          // debugger;
                          // if (preventDateObj && fromDate && fromDate < preventDateObj) {
                          //   // Don't allow selection before preventDate
                          //   return;
                          // }

                          setFrom(e.from);
                          setTo(e.to);
                          if (e.to) {
                            updateDateRange(e.from, e.to, 'date');
                          }
                        }}
                        onOk={() => {
                          setAnchorEl(null);
                          setFrom(null);
                          setTo(null);
                        }}
                      />
                    </Paper>
                  </Popper>
                )}
              </Select>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: { xs: 'center', sm: 'flex-start' },
            height: '100%',
            width: { xs: '100%', sm: 'auto' },
            ml: 0,
          }}
        >
          {outstandingPeriod ? (
            <Typography
              variant="body1"
              sx={{
                marginLeft: { xs: '0px', sm: '5px' },
                fontSize: { xs: '10px !important', sm: '14px !important' },
                // marginTop: '4px',
                color: theme?.palette?.TmId.id.toString().includes('9') ? 'white' : 'black',
              }}
            >
              {showDateRangeText && dateText}
            </Typography>
          ) : (
            <>
              <Typography
                variant="body2"
                sx={{
                  m: 0,
                  p: 0,
                  fontSize: { xs: '12px', sm: '14px' },
                  textAlign: { xs: 'center', sm: 'left' },
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                {title}:<span style={{ fontWeight: 'bold', marginLeft: '4px' }}>{formatPeriodText(startTimeStamp, endTimeStamp)}</span>
              </Typography>
              {openingBalanceRequired === true ? (
                <FormControlLabel
                  control={
                    <Checkbox
                      key={page + title}
                      id={`checkbox-${page}-${title}`}
                      size="small"
                      checked={includeOpeningBalance}
                      onChange={(e) => setIncludeOpeningBalance(e.target.checked)}
                      sx={{
                        m: 0,
                        p: 0,
                      }}
                    />
                  }
                  label={
                    // selectedTab === 9
                    //   ? matches
                    //     ? 'Opening Stock'
                    //     : 'Include Opening Stock'
                    //   :
                    matches ? 'Opening Balance' : 'Include Opening Balance'
                  }
                  labelPlacement="end"
                  sx={{
                    m: 0,
                    '& .MuiFormControlLabel-label': {
                      fontSize: { xs: '12px', sm: '14px' },
                    },
                  }}
                />
              ) : null}
            </>
          )}
        </Box>
      </Box>
      <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'flex-start', mt: '-15px' }}>
        <Typography
          variant="body2"
          sx={{
            p: 0,
            fontSize: { xs: '12px', sm: '14px' },
          }}
        >
          <span style={{ fontWeight: 'bold', marginLeft: '4px' }}>{formatPeriodText(startTimeStamp, endTimeStamp)}</span>
        </Typography>
      </Box>
    </Box>
  );
};

export default ActionItems;
